import { useId, type InputHTMLAttributes, type ReactNode } from 'react';

import { cx } from '../../lib/class-names';

import styles from './FormField.module.css';

interface FormFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  /** Guidance shown under the control and announced with the input. */
  hint?: ReactNode;
  error?: string;
  variant?: 'default' | 'auth';
}

/**
 * A labelled text input.
 *
 * The label is a real `<label for>` rather than a placeholder, so it stays
 * visible while typing and is read out on focus. Hint and error text are wired
 * through `aria-describedby`, and `aria-invalid` marks the field itself, which
 * is what a screen reader uses to announce the problem — colour alone would
 * not.
 */
export function FormField({
  label,
  hint,
  error,
  className,
  required,
  variant = 'default',
  ...inputProps
}: FormFieldProps): React.JSX.Element {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const isAuth = variant === 'auth';

  const describedBy =
    [hint === undefined ? null : hintId, error === undefined ? null : errorId]
      .filter((value): value is string => value !== null)
      .join(' ') || undefined;

  return (
    <div className={cx(styles.field, isAuth && styles.fieldAuth, className)}>
      <label className={cx(styles.label, isAuth && styles.labelAuth)} htmlFor={id}>
        {label}
        {required === true && (
          <span className={styles.required} aria-hidden="true">
            {' '}
            *
          </span>
        )}
        {required === true && <span className="visually-hidden"> (required)</span>}
      </label>

      <input
        {...inputProps}
        id={id}
        required={required}
        aria-invalid={error === undefined ? undefined : true}
        aria-describedby={describedBy}
        className={cx(
          styles.input,
          isAuth && styles.inputAuth,
          error !== undefined && styles.inputInvalid,
          error !== undefined && isAuth && styles.inputInvalidAuth,
        )}
      />

      {hint !== undefined && (
        <p className={cx(styles.hint, isAuth && styles.hintAuth)} id={hintId}>
          {hint}
        </p>
      )}

      {error !== undefined && (
        <p className={cx(styles.error, isAuth && styles.errorAuth)} id={errorId}>
          {error}
        </p>
      )}
    </div>
  );
}
